import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { paymentService, SubscriptionPlan, PaymentOrder } from '../services/paymentService';

interface PaymentState {
  selectedPlan: SubscriptionPlan | null;
  order: PaymentOrder | null;
  loading: boolean;
  error: string | null;
  paymentSummaryVisible: boolean;
}

interface PaymentContextType extends PaymentState {
  selectPlan: (plan: SubscriptionPlan) => void;
  createOrder: (plan: SubscriptionPlan) => Promise<void>;
  verifyPayment: (orderId: string, paymentId: string, signature: string) => Promise<boolean>;
  clearError: () => void;
  resetPayment: () => void;
}

type PaymentAction =
  | { type: 'SELECT_PLAN'; payload: SubscriptionPlan }
  | { type: 'CREATE_ORDER_START' }
  | { type: 'CREATE_ORDER_SUCCESS'; payload: PaymentOrder }
  | { type: 'CREATE_ORDER_FAILURE'; payload: string }
  | { type: 'VERIFY_PAYMENT_START' }
  | { type: 'VERIFY_PAYMENT_SUCCESS' }
  | { type: 'VERIFY_PAYMENT_FAILURE'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESET_PAYMENT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'TOGGLE_PAYMENT_SUMMARY'; payload: boolean };

const initialState: PaymentState = {
  selectedPlan: null,
  order: null,
  loading: false,
  error: null,
  paymentSummaryVisible: false,
};

const paymentReducer = (state: PaymentState, action: PaymentAction): PaymentState => {
  switch (action.type) {
    case 'SELECT_PLAN':
      return {
        ...state,
        selectedPlan: action.payload,
        paymentSummaryVisible: true,
        error: null,
      };
    
    case 'CREATE_ORDER_START':
    case 'VERIFY_PAYMENT_START':
      return { ...state, loading: true, error: null };
    
    case 'CREATE_ORDER_SUCCESS':
      return {
        ...state,
        loading: false,
        order: action.payload,
        error: null,
      };
    
    case 'CREATE_ORDER_FAILURE':
    case 'VERIFY_PAYMENT_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    
    case 'VERIFY_PAYMENT_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
      };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    case 'RESET_PAYMENT':
      return {
        ...state,
        selectedPlan: null,
        order: null,
        loading: false,
        error: null,
        paymentSummaryVisible: false,
      };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'TOGGLE_PAYMENT_SUMMARY':
      return { ...state, paymentSummaryVisible: action.payload };
    
    default:
      return state;
  }
};

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

interface PaymentProviderProps {
  children: ReactNode;
}

export const PaymentProvider: React.FC<PaymentProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);

  const selectPlan = (plan: SubscriptionPlan) => {
    dispatch({ type: 'SELECT_PLAN', payload: plan });
  };

  const createOrder = async (plan: SubscriptionPlan) => {
    dispatch({ type: 'CREATE_ORDER_START' });
    try {
      const response = await paymentService.createOrder({
        amount: plan.price,
        currency: 'INR',
        receipt: `rcpt_${Date.now()}_${plan.type}`,
        notes: {
          plan_type: plan.type,
          timestamp: new Date().toISOString()
        }
      });

      if (response.success && response.order) {
        dispatch({ type: 'CREATE_ORDER_SUCCESS', payload: response.order });
      } else {
        dispatch({ type: 'CREATE_ORDER_FAILURE', payload: 'Failed to create payment order' });
      }
    } catch (error: any) {
      dispatch({ 
        type: 'CREATE_ORDER_FAILURE', 
        payload: error.response?.data?.error || 'Failed to create payment order' 
      });
    }
  };

  const verifyPayment = async (orderId: string, paymentId: string, signature: string): Promise<boolean> => {
    dispatch({ type: 'VERIFY_PAYMENT_START' });
    try {
      const response = await paymentService.verifyPayment({
        order_id: orderId,
        payment_id: paymentId,
        signature
      });

      if (response.success && response.verified) {
        dispatch({ type: 'VERIFY_PAYMENT_SUCCESS' });
        return true;
      } else {
        dispatch({ 
          type: 'VERIFY_PAYMENT_FAILURE', 
          payload: response.error || 'Payment verification failed' 
        });
        return false;
      }
    } catch (error: any) {
      dispatch({ 
        type: 'VERIFY_PAYMENT_FAILURE', 
        payload: error.response?.data?.error || 'Payment verification failed' 
      });
      return false;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const resetPayment = () => {
    dispatch({ type: 'RESET_PAYMENT' });
  };

  const value: PaymentContextType = {
    ...state,
    selectPlan,
    createOrder,
    verifyPayment,
    clearError,
    resetPayment,
  };

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

export const usePayment = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};
